/**
 * 清溪天成模版 - 注册页
 * 
 * 设计：三步注册向导（圆点进度条）
 * 独特排版：使用横向步骤指示器（圆点+连线），分三步完成注册
 */

import { useState, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function CrystalRegisterPage() {
  const navigate = useNavigate();
  const { siteId } = useSite();
  const { siteConfig } = useSiteConfig();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    username: "", password: "", confirmPassword: "",
    nickname: "", phone: "", email: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);

  const siteName = siteConfig?.site_name || siteConfig?.name || "";
  const logo = siteConfig?.logo || "";

  const steps = [
    { num: 1, title: "账户信息", desc: "设置登录凭据" },
    { num: 2, title: "个人资料", desc: "完善基本信息" },
    { num: 3, title: "确认提交", desc: "同意协议并注册" },
  ];

  const canNextStep1 = formData.username.trim() && formData.password.trim() && formData.confirmPassword.trim();
  const canNextStep2 = true;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.username.trim() || !formData.password.trim()) {
      setError("请填写用户名和密码");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("两次输入的密码不一致");
      return;
    }
    if (formData.password.length < 6) {
      setError("密码长度至少6位");
      return;
    }
    if (!agreeTerms) {
      setError("请先阅读并同意服务协议");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          nickname: formData.nickname || formData.username,
          phone: formData.phone,
          email: formData.email,
          site_id: siteId,
        }),
      });
      const result = await response.json();
      if (result.code !== 0) {
        setError(result.message || "注册失败，请重试");
      } else {
        setSuccess("注册成功！正在跳转到登录页...");
        setTimeout(() => navigate("/login"), 2000);
      }
    } catch (err: any) {
      setError(err?.message || "网络错误，请重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-gradient-to-b from-cyan-50/60 via-white to-blue-50/30 py-8">
      {/* 装饰 */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full border border-cyan-200/20" />
        <div className="absolute bottom-0 left-0 w-[350px] h-[350px] rounded-full border border-blue-200/20" />
      </div>

      <div className="relative z-10 w-full max-w-lg mx-auto px-4">
        <div className="bg-white/80 backdrop-blur-2xl rounded-3xl shadow-2xl shadow-cyan-500/10 border border-cyan-100/30 p-8 sm:p-10">
          {/* Logo */}
          <div className="text-center mb-6">
            {logo ? (
              <img src={logo} alt={siteName} className="h-10 mx-auto mb-2" />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-500 mx-auto flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-cyan-400/30 mb-2">
                {siteName ? siteName.charAt(0).toUpperCase() : "C"}
              </div>
            )}
            <h1 className="text-xl font-bold text-gray-800">注册{siteName}</h1>
          </div>

          {/* 步骤指示器 */}
          <div className="flex items-center justify-center mb-8">
            {steps.map((s, idx) => (
              <div key={s.num} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      step >= s.num
                        ? "bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-md shadow-cyan-500/25"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    {step > s.num ? <i className="ri-check-line" /> : s.num}
                  </div>
                  <span className={`text-[10px] mt-1 font-medium ${step >= s.num ? "text-cyan-600" : "text-gray-300"}`}>
                    {s.title}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`w-10 h-0.5 mx-1 mb-5 rounded-full transition-all ${step > s.num ? "bg-cyan-400" : "bg-gray-200"}`} />
                )}
              </div>
            ))}
          </div>

          {error && (
            <div className="mb-5 p-3.5 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 flex items-center gap-2">
              <i className="ri-error-warning-line" />{error}
            </div>
          )}

          {success && (
            <div className="mb-5 p-3.5 bg-green-50 border border-green-100 rounded-xl text-sm text-green-600 flex items-center gap-2">
              <i className="ri-check-line" />{success}
            </div>
          )}

          {/* Step 1: 账户信息 */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">用户名 *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="用于登录"
                  className="w-full px-4 py-3 bg-cyan-50/30 border border-cyan-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-500/10 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">密码 *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="至少6位"
                  className="w-full px-4 py-3 bg-cyan-50/30 border border-cyan-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-500/10 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">确认密码 *</label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="再次输入密码"
                  className="w-full px-4 py-3 bg-cyan-50/30 border border-cyan-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-500/10 transition-all"
                />
              </div>
              <button
                type="button"
                onClick={() => { if (canNextStep1) setStep(2); else setError("请完整填写账户信息"); }}
                className="w-full py-3.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold rounded-2xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg shadow-cyan-500/25 text-sm"
              >
                下一步
              </button>
            </div>
          )}

          {/* Step 2: 个人资料 */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">昵称</label>
                <input
                  type="text"
                  value={formData.nickname}
                  onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                  placeholder="显示名称（选填）"
                  className="w-full px-4 py-3 bg-cyan-50/30 border border-cyan-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-500/10 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">手机号</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="方便联系（选填）"
                  className="w-full px-4 py-3 bg-cyan-50/30 border border-cyan-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-500/10 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">电子邮箱</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="接收通知（选填）"
                  className="w-full px-4 py-3 bg-cyan-50/30 border border-cyan-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-500/10 transition-all"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-3.5 border-2 border-gray-200 text-gray-600 font-semibold rounded-2xl hover:bg-gray-50 transition-all text-sm"
                >
                  上一步
                </button>
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="flex-1 py-3.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold rounded-2xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg shadow-cyan-500/25 text-sm"
                >
                  下一步
                </button>
              </div>
            </div>
          )}

          {/* Step 3: 确认 */}
          {step === 3 && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="p-4 bg-cyan-50/40 rounded-xl text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">用户名</span>
                  <span className="text-gray-700 font-semibold">{formData.username}</span>
                </div>
                {formData.nickname && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">昵称</span>
                    <span className="text-gray-700">{formData.nickname}</span>
                  </div>
                )}
                {formData.phone && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">手机</span>
                    <span className="text-gray-700">{formData.phone}</span>
                  </div>
                )}
                {formData.email && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">邮箱</span>
                    <span className="text-gray-700">{formData.email}</span>
                  </div>
                )}
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="w-4 h-4 rounded accent-cyan-500 mt-0.5 shrink-0"
                />
                <span className="text-xs text-gray-500 leading-relaxed">
                  我已阅读并同意
                  <Link to="/page/register-agreement" className="text-cyan-600 hover:text-cyan-700 mx-1">服务协议</Link>
                  和
                  <Link to="/page/privacy" className="text-cyan-600 hover:text-cyan-700 ml-1">隐私政策</Link>
                </span>
              </label>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="flex-1 py-3.5 border-2 border-gray-200 text-gray-600 font-semibold rounded-2xl hover:bg-gray-50 transition-all text-sm"
                >
                  上一步
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold rounded-2xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg shadow-cyan-500/25 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />注册中...
                    </span>
                  ) : "创建账户"}
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 text-center flex items-center justify-between">
            <Link to="/" className="text-sm text-gray-400 hover:text-cyan-600 transition-colors inline-flex items-center gap-1">
              <i className="ri-arrow-left-line" />返回首页
            </Link>
            <p className="text-sm text-gray-400">
              已有账号？{" "}
              <Link to="/login" className="text-cyan-600 hover:text-cyan-700 font-semibold">登录</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
